import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface CheckoutPreviewFrameProps {
  children: React.ReactNode;
  device: 'desktop' | 'tablet' | 'mobile';
  className?: string;
  refreshKey?: number;
}

export const CheckoutPreviewFrame: React.FC<CheckoutPreviewFrameProps> = ({
  children,
  device,
  className = '',
  refreshKey = 0,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [mountNode, setMountNode] = useState<HTMLElement | null>(null);
  const [isReady, setIsReady] = useState<boolean>(false);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let isMounted = true;
    setIsReady(false);
    setMountNode(null);

    const setupIframe = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) return;

        doc.open();
        doc.write(`
          <!DOCTYPE html>
          <html lang="en">
            <head>
              <meta charset="UTF-8" />
              <meta name="viewport" content="width=device-width, initial-scale=1.0" />
              <script src="https://cdn.tailwindcss.com"></script>
              <script>
                tailwind.config = {
                  darkMode: 'class',
                  theme: {
                    extend: {
                      colors: {
                        slate: {
                          850: '#1e293b',
                          900: '#0f172a',
                          950: '#020617',
                        }
                      }
                    }
                  }
                };
              </script>
              <style>
                * {
                  box-sizing: border-box;
                }
                html, body {
                  margin: 0;
                  padding: 0;
                  width: 100%;
                  min-height: 100%;
                  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                  background-color: transparent;
                }
                /* Clean scrollbar */
                ::-webkit-scrollbar {
                  width: 5px;
                  height: 5px;
                }
                ::-webkit-scrollbar-track {
                  background: transparent;
                }
                ::-webkit-scrollbar-thumb {
                  background: rgba(148, 163, 184, 0.4);
                  border-radius: 9999px;
                }
                ::-webkit-scrollbar-thumb:hover {
                  background: rgba(148, 163, 184, 0.7);
                }
              </style>
            </head>
            <body>
              <div id="checkout-preview-root" style="min-height: 100vh; display: flex; flex-direction: column;"></div>
            </body>
          </html>
        `);
        doc.close();

        const attachRoot = () => {
          if (!isMounted) return;
          const root = doc.getElementById('checkout-preview-root');
          if (root) {
            setMountNode(root);
            setIsReady(true);
          } else {
            setTimeout(attachRoot, 15);
          }
        };
        attachRoot();
      } catch (err) {
        console.error('Error initializing checkout preview iframe:', err);
      }
    };

    setupIframe();

    return () => {
      isMounted = false;
    };
  }, [device, refreshKey]);

  return (
    <div className={`w-full h-full relative ${className}`}>
      {!isReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/60 text-slate-400 text-xs font-semibold z-10">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 animate-spin text-indigo-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
            <span>Rendering {device} preview...</span>
          </div>
        </div>
      )}
      <iframe
        ref={iframeRef}
        title="Checkout Preview"
        className="w-full h-full border-0 bg-transparent block"
        style={{ colorScheme: 'light dark' }}
      />
      {mountNode && createPortal(children, mountNode)}
    </div>
  );
};
